import React, { createContext, useState, useContext, useEffect } from 'react';

// 定义主题类型
export type ThemeType = 'default' | 'light' | 'vibrant' | 'gradient';

// 主题上下文接口
interface ThemeContextType {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
}

// 创建主题上下文
const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

// 主题提供者组件
export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 从本地存储中获取主题，如果没有则使用默认主题
  const [theme, setTheme] = useState<ThemeType>(() => {
    const savedTheme = localStorage.getItem('ai-painting-expert-theme');
    return (savedTheme as ThemeType) || 'default';
  });

  // 当主题变化时，更新本地存储和文档类名
  useEffect(() => {
    localStorage.setItem('ai-painting-expert-theme', theme);
    
    // 移除所有主题类名
    document.documentElement.classList.remove('theme-default', 'theme-light', 'theme-vibrant', 'theme-gradient');
    
    // 添加当前主题类名
    document.documentElement.classList.add(`theme-${theme}`);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

// 自定义钩子，用于在组件中访问主题
export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
